import type { CaseStepInfoItem, CaseStepStateItem, StepInOrderItem } from "services/cases.service";
import { normalizeCaseState } from "../../../utils";
import { PRE_CONFIRM_STATES } from "./constants";

export function stepInOrderToDisplayItem(step: StepInOrderItem): CaseStepInfoItem {
  const tasks = step.tasks ?? [];
  return {
    step_info: {
      process: step.process,
      process_id: step.process_id,
      step_id: step.step_id,
      name: step.name,
      description: step.description,
      todolist_id: step.todolist_id,
      tasks,
    },
    task_details: {
      case_step_todolist: tasks.map((t) => ({ ...t, is_checked: false })),
      case_step_todolist_info: { total_tasks: tasks.length, completed: 0 },
    },
    current_state: { state_name: "NOT STARTED" },
  };
}

export function isCaseConfirmed(case_curr_state?: string): boolean {
  const state = (case_curr_state ?? "").toUpperCase().trim().replace(/\s+/g, "_");
  return !PRE_CONFIRM_STATES.some((s) => state === s.replace(/-/g, "_"));
}

export function isCaseCreated(case_curr_state?: string): boolean {
  const state = (case_curr_state ?? "").toUpperCase().trim().replace(/\s+/g, "_");
  return PRE_CONFIRM_STATES.some((s) => state === s.replace(/-/g, "_"));
}

export function isSwitchProcessAllowed(case_curr_state?: string): boolean {
  const s = normalizeCaseState(case_curr_state);
  return s === "DRAFT" || s === "INITIATED" || s === "CREATED";
}

export function isCaseInProgress(case_curr_state?: string): boolean {
  const state = (case_curr_state ?? "").toUpperCase().trim();
  const normalized = state.replace(/\s+/g, "-");
  return (
    normalized === "IN-PROGRESS" || state === "INPROGRESS" || state.replace(/-/g, "_") === "PENDING"
  );
}

export function isCaseCompleted(case_curr_state?: string): boolean {
  const state = (case_curr_state ?? "").toUpperCase().trim().replace(/\s+/g, "-");
  return state === "COMPLETED";
}

export function isCaseClosed(case_curr_state?: string): boolean {
  const state = (case_curr_state ?? "").toUpperCase().trim().replace(/\s+/g, "-");
  return state === "CLOSED";
}

export function isCaseDetailsReadOnly(case_curr_state?: string): boolean {
  return isCaseCompleted(case_curr_state) || isCaseClosed(case_curr_state);
}

export function isCaseOnHold(case_curr_state?: string): boolean {
  const s = normalizeCaseState(case_curr_state);
  return s === "ON-HOLD" || s === "ONHOLD";
}

export function isCaseQandAReadOnly(case_curr_state?: string): boolean {
  return (
    isCaseDetailsReadOnly(case_curr_state) ||
    isCaseInProgress(case_curr_state) ||
    isCaseOnHold(case_curr_state)
  );
}

export function formatStateName(stateName?: string): string {
  if (!stateName) return "Not started";
  const lower = stateName.toLowerCase().replace(/_/g, " ");
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export function normalizeStepStateKey(stateName?: string): string {
  return (stateName ?? "").toUpperCase().trim().replace(/\s+/g, "_").replace(/-/g, "_");
}

export function caseStepStateNameToBadgeColor(
  stateName?: string
): "gray" | "brand" | "success" | "warning" | "error" {
  const n = normalizeStepStateKey(stateName);
  if (!n) return "gray";
  if (n === "COMPLETED" || (n.includes("COMPLET") && !n.includes("PROGRESS"))) return "success";
  if (n.includes("HOLD")) return "warning";
  if (n === "NOT_STARTED") return "gray";
  if (n.includes("PROGRESS") || n.includes("STARTED") || n === "IN_PROGRESS" || n === "WIP")
    return "brand";
  return "gray";
}

function findStepStateByKeys(
  states: CaseStepStateItem[] | undefined,
  keys: string[]
): CaseStepStateItem | undefined {
  if (!states?.length) return undefined;
  const keySet = new Set(keys.map((k) => normalizeStepStateKey(k)));
  return states.find((s) => keySet.has(normalizeStepStateKey(s.state_name)));
}

export function isCompletedLikeStepStateName(stateName?: string): boolean {
  const n = normalizeStepStateKey(stateName);
  if (!n) return false;
  if (n === "COMPLETED" || n === "COMPLETE" || n === "DONE") return true;
  return n.includes("COMPLET") && !n.includes("PROGRESS");
}

export function isStepWorkflowCompletedItem(step: CaseStepInfoItem): boolean {
  if (step.is_completed === true) return true;
  const rawState = normalizeStepStateKey(step.current_state?.state_name);
  if (rawState === "COMPLETED") return true;
  return isCompletedLikeStepStateName(step.current_state?.state_name);
}

export function findCompletedStepState(
  states: CaseStepStateItem[] | undefined
): CaseStepStateItem | undefined {
  if (!states?.length) return undefined;
  const completed = findStepStateByKeys(states, ["COMPLETED", "COMPLETE", "DONE"]);
  if (completed) return completed;
  return states.find((s) => {
    const n = normalizeStepStateKey(s.state_name);
    return n.includes("COMPLET") && !n.includes("PROGRESS");
  });
}

export function findInProgressStepState(
  states: CaseStepStateItem[] | undefined
): CaseStepStateItem | undefined {
  if (!states?.length) return undefined;
  const nonTerminal = states.filter((s) => !isCompletedLikeStepStateName(s.state_name));
  if (!nonTerminal.length) return undefined;

  const withoutNotStarted = nonTerminal.filter((s) => {
    const n = normalizeStepStateKey(s.state_name);
    return n !== "NOT_STARTED" && n !== "NOTSTARTED";
  });
  const pool = withoutNotStarted.length ? withoutNotStarted : nonTerminal;

  const fromKeys = findStepStateByKeys(pool, [
    "IN_PROGRESS",
    "INPROGRESS",
    "WIP",
    "ACTIVE",
    "STARTED_WORKING",
    "STARTED_WORK",
  ]);
  if (fromKeys) return fromKeys;
  return pool.find((s) => {
    const n = normalizeStepStateKey(s.state_name);
    return (
      (n.includes("START") && !n.includes("NOT")) ||
      (n.includes("PROGRESS") && !n.includes("COMPLET"))
    );
  });
}
